/**
 * MathJax Processing Queue System
 *
 * Prevents MathJax processing interruptions during streaming content updates
 * by queuing content changes and processing them sequentially.
 */

// Type declarations for MathJax
declare global {
  interface Window {
    MathJax: {
      startup?: {
        adaptor?: {
          outerHTML?: (svg: SVGSVGElement) => string
        }
        defaultReady?: () => void
        ready?: () => void
      }
      svg?: object
      tex?: object
      tex2svgPromise?: (
        latex: string,
        options?: { display?: boolean }
      ) => Promise<SVGSVGElement>
      typesetPromise?: (elements: Element[]) => Promise<void>
    }
  }
}

interface ProcessingTask {
  container: HTMLDivElement
  content: string
  reject: (error: Error) => void
  resolve: (result: boolean) => void
}

class MathJaxProcessor {
  private static instance: MathJaxProcessor
  private isProcessing = false
  private loadPromise: null | Promise<Required<typeof window.MathJax>> = null
  private processingQueue: ProcessingTask[] = []

  private constructor() {}

  static getInstance(): MathJaxProcessor {
    if (!MathJaxProcessor.instance) {
      MathJaxProcessor.instance = new MathJaxProcessor()
    }
    return MathJaxProcessor.instance
  }

  /**
   * Process MathJax elements with interruption protection
   */
  async processContainer(
    container: HTMLDivElement,
    content: string
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const task: ProcessingTask = {
        container,
        content,
        reject,
        resolve
      }

      this.processingQueue.push(task)
      this.processQueue()
    })
  }

  /**
   * Load MathJax if not already loaded
   */
  private async loadMathJax(): Promise<Required<typeof window.MathJax>> {
    if (this.loadPromise) {
      return this.loadPromise
    }

    this.loadPromise = new Promise<Required<typeof window.MathJax>>(
      (resolve) => {
        if (document.getElementById("MathJax-script")) {
          return resolve(window.MathJax as Required<typeof window.MathJax>)
        }

        window.MathJax = {
          startup: {
            ready: () => {
              window.MathJax.startup?.defaultReady?.()
              resolve(window.MathJax as Required<typeof window.MathJax>)
            }
          },
          svg: {
            fontCache: "local"
          },
          tex: {
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"]
            ],
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"]
            ]
          }
        }

        const script = document.createElement("script")
        script.type = "text/javascript"
        script.id = "MathJax-script"
        script.async = true
        script.src = "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js"
        document.head.appendChild(script)
      }
    )

    return this.loadPromise
  }

  /**
   * Process the queue sequentially to prevent interruptions
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.processingQueue.length === 0) {
      return
    }

    this.isProcessing = true

    try {
      const mathjax = await this.loadMathJax()

      while (this.processingQueue.length > 0) {
        const task = this.processingQueue.shift()!

        try {
          const success = await this.processTask(mathjax, task)

          task.resolve(success)
        } catch (error) {
          task.reject(error instanceof Error ? error : new Error(String(error)))
        }
      }
    } catch (error) {
      // Reject all pending tasks
      while (this.processingQueue.length > 0) {
        const task = this.processingQueue.shift()!
        task.reject(error instanceof Error ? error : new Error(String(error)))
      }
    } finally {
      this.isProcessing = false
    }
  }

  /**
   * Process a single task with completion detection
   */
  private async processTask(
    mathjax: Required<typeof window.MathJax>,
    task: ProcessingTask
  ): Promise<boolean> {
    const texSpans = task.container.querySelectorAll("span[data-tex]")
    if (texSpans.length === 0) {
      return true
    }

    const parseLatex = (raw: string): { display: boolean; latex: string } => {
      const value = raw.trim()
      if (
        (value.startsWith("$$") && value.endsWith("$$")) ||
        (value.startsWith("\\[") && value.endsWith("\\]"))
      ) {
        const latex = value.replace(/^\$\$|^\\\[|\$\$$|\\\]$/g, "").trim()
        return { display: true, latex }
      }
      if (
        (value.startsWith("$") && value.endsWith("$")) ||
        (value.startsWith("\\(") && value.endsWith("\\)"))
      ) {
        const latex = value
          .replace(/^\$|^\\\(|\$$/g, "")
          .replace(/\\\)$/g, "")
          .trim()
        return { display: false, latex }
      }
      return { display: false, latex: value }
    }

    await Promise.all(
      Array.from(texSpans).map(async (span) => {
        // Skip spans that already have SVG content
        if (span.querySelector("svg")) {
          span.setAttribute("data-mathjax-rendered", "true")
          return
        }

        const tex = span.getAttribute("data-tex")
        if (!tex) return

        try {
          const { display, latex } = parseLatex(tex)

          if (typeof mathjax.tex2svgPromise === "function") {
            const svg = await mathjax.tex2svgPromise(latex, { display })
            span.innerHTML = ""
            span.appendChild(svg)
          } else if (typeof mathjax.typesetPromise === "function") {
            await mathjax.typesetPromise([span])
          }

          const svg = span.querySelector("svg")
          if (svg) {
            const width = svg.getAttribute("width")
            const height = svg.getAttribute("height")
            if (width?.includes("NaN") || height?.includes("NaN")) {
              throw new Error(`Invalid SVG dimensions for LaTeX: ${tex}`)
            }
            span.setAttribute("data-mathjax-rendered", "true")
          }
        } catch (error) {
          console.warn(`Failed to render LaTeX: ${tex}`, error)
          span.innerHTML = tex
          span.removeAttribute("data-mathjax-rendered")
          throw error
        }
      })
    )

    return true
  }
}

export const mathJaxProcessor = MathJaxProcessor.getInstance()
